// CSV serialization for the address-book export (register item 11).
// PURE — no DOM — so the escaping rules are unit-testable. The browser-only
// download step lives in the view that calls this.
//
// Scope note: Kaycia asked about exporting the address book AND about emailing
// several clients at once. Only the export is built. Bulk sending carries
// deliverability, unsubscribe and privacy consequences that belong in a real
// email tool, and the export very likely satisfies the actual need.

/**
 * RFC 4180 escaping. A field is quoted when it contains a comma, a quote, a
 * newline, or leading/trailing spaces that a spreadsheet would otherwise eat;
 * embedded quotes are doubled.
 *
 * Formula injection: contact fields arrive from the public website forms, and a
 * cell starting with = + - @ (or tab/CR) runs as a formula when the export is
 * opened in Excel or Sheets. Such cells get a leading apostrophe so they stay
 * text. Pure numbers and phone numbers ("+1 (555) 010-2000") are left alone —
 * digits and phone punctuation cannot call a function, and an apostrophe on
 * every phone number would make the export look broken.
 */
const FORMULA_LEAD = /^[=+\-@\t\r]/
const NUMERIC_ONLY = /^[+-]?[\d\s().-]+$/

export function csvCell(value) {
  if (value === null || value === undefined) return ''
  let s = String(value)
  if (FORMULA_LEAD.test(s) && !NUMERIC_ONLY.test(s)) s = `'${s}`
  const needsQuotes = /[",\r\n]/.test(s) || s !== s.trim()
  return needsQuotes ? `"${s.replace(/"/g, '""')}"` : s
}

export function toCsv(headers = [], rows = []) {
  const lines = [headers.map(csvCell).join(',')]
  rows.forEach((row) => lines.push(row.map(csvCell).join(',')))
  // CRLF: Excel is the likeliest destination and it is the RFC's line ending.
  return lines.join('\r\n')
}

export const CONTACT_EXPORT_HEADERS = [
  'First name', 'Last name', 'Email', 'Phone', 'Job title', 'Company', 'Lifecycle stage',
]

/** One CSV row per contact, company name resolved from the id map. */
export function contactsToCsv(contacts = [], companiesById = {}) {
  const rows = contacts.map((c) => [
    c.firstName || '',
    c.lastName || '',
    c.email || '',
    c.phone || '',
    c.title || '',
    companiesById[c.primaryCompanyId]?.name || '',
    c.lifecycleStage || '',
  ])
  return toCsv(CONTACT_EXPORT_HEADERS, rows)
}

/** Stable, sortable filename — no clock reads inside, the caller passes the date. */
export function exportFilename(prefix, date = new Date()) {
  const pad = (n) => String(n).padStart(2, '0')
  return `${prefix}-${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}.csv`
}
